import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { 
  GraduationCap, 
  MapPin, 
  DollarSign, 
  Book, 
  Calendar, 
  CheckCircle, 
  ExternalLink,
  ArrowLeft, 
  School 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface University {
  name: string;
  country: string;
  programs: string[];
  hasScholarship: boolean;
  annualTuition: number;
  scholarshipDetails?: string;
  websiteUrl?: string;
}

const ScholarshipDetailsPage = () => {
  const { universityName } = useParams();
  const [university, setUniversity] = useState<University | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Function to validate and format university website URLs
  const getValidWebsiteUrl = (university: University): string => {
    // If websiteUrl is provided and valid, use it
    if (university.websiteUrl) {
      try {
        new URL(university.websiteUrl);
        return university.websiteUrl;
      } catch (e) {
        // If not valid, fall through to generate a URL
      }
    }
    
    // Generate a standardized URL based on university name
    const name = university.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Determine appropriate domain extension based on country
    let domain = ".edu";
    if (university.country) {
      const country = university.country.toLowerCase();
      if (country.includes("united kingdom") || country.includes("uk")) {
        domain = ".ac.uk";
      } else if (country.includes("canada")) {
        domain = ".ca";
      } else if (country.includes("australia")) {
        domain = ".edu.au";
      } else if (country.includes("germany")) {
        domain = ".de";
      } else if (country.includes("france")) {
        domain = ".fr";
      } else if (country.includes("japan")) {
        domain = ".jp";
      } else if (country.includes("china")) {
        domain = ".cn";
      } else if (country.includes("india")) {
        domain = ".ac.in";
      }
    }
    
    return `https://www.${name}${domain}`;
  };

  useEffect(() => {
    if (!universityName) return;

    const fetchUniversityDetails = async () => {
      setIsLoading(true);
      try {
        // Use OpenAI to generate detailed information about this specific university
        const decodedName = decodeURIComponent(universityName);
        
        console.log("Fetching details for university:", decodedName);
        
        const { data, error } = await supabase.functions.invoke('search-scholarships', {
          body: {
            universityName: decodedName,
            detailedView: true
          }
        });
        
        if (error) {
          console.error("Supabase function error:", error);
          throw new Error(error.message);
        }
        
        console.log("University details response:", data);
        
        if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
          setUniversity(data.data[0]);
        } else if (data && data.data && !Array.isArray(data.data)) {
          // Handle case where data is returned as a single object instead of an array
          setUniversity(data.data);
        } else {
          // Fallback fake data if API doesn't return good results
          setUniversity({
            name: decodedName,
            country: "International",
            programs: ["Various Programs"],
            hasScholarship: true,
            annualTuition: 25000,
            scholarshipDetails: "Various scholarships available. Contact the university for specific details.",
            websiteUrl: `https://www.${decodedName.toLowerCase().replace(/[^a-z0-9]/g, '')}.edu`
          });
        }
      } catch (error) {
        console.error("Error fetching university details:", error);
        toast({
          title: "Error",
          description: "Failed to load university details. Please try again.",
          variant: "destructive",
        });
        
        // Set fallback data
        setUniversity({
          name: decodeURIComponent(universityName),
          country: "International",
          programs: ["Various Programs"],
          hasScholarship: true,
          annualTuition: 25000,
          scholarshipDetails: "Various scholarships available. Contact the university for specific details.",
          websiteUrl: `https://www.${decodeURIComponent(universityName).toLowerCase().replace(/[^a-z0-9]/g, '')}.edu`
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUniversityDetails();
  }, [universityName, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-64 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!university) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold mb-4">University Not Found</h1>
            <p className="mb-6">The university details you're looking for could not be found.</p>
            <Link to="/scholarships">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Scholarships Search
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const universityWebsiteUrl = getValidWebsiteUrl(university);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{university.name} - Scholarship Details | Visanow</title>
      </Helmet>
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/scholarships" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Scholarships Search
          </Link>
          
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 mb-8 text-white">
            <div className="flex items-center gap-3">
              <School className="h-8 w-8" />
              <h1 className="text-3xl font-bold">{university.name}</h1>
            </div>
            <div className="flex items-center mt-2 gap-2">
              <MapPin className="h-5 w-5" />
              <span className="text-lg">{university.country}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Annual Tuition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">${university.annualTuition.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-1">USD per academic year</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Book className="h-5 w-5 text-blue-600" />
                  Programs Offered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {university.programs.map((program, i) => (
                    <span key={i} className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      {program}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                  Scholarship Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {university.hasScholarship ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">Scholarships Available</span>
                  </div>
                ) : (
                  <div className="text-gray-500">No scholarships specified</div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Scholarship Details</CardTitle>
              <CardDescription>Information about available financial aid options</CardDescription>
            </CardHeader>
            <CardContent>
              {university.scholarshipDetails ? (
                <div className="prose max-w-none">
                  <p>{university.scholarshipDetails}</p>
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Application Deadlines
                    </h3>
                    <p className="text-sm text-blue-700">
                      Scholarship application deadlines vary. Please check the university website for specific dates and requirements.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No detailed scholarship information available.</p>
              )}
            </CardContent>
          </Card>
          
          <div className="text-center mb-8">
            <a 
              href={universityWebsiteUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Button size="lg" className="gap-2">
                Visit University Website
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Next Steps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                <h3 className="font-semibold mb-2">Contact Admissions</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Reach out to the university's admissions office for specific scholarship information.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                <h3 className="font-semibold mb-2">Prepare Documents</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Gather transcripts, recommendation letters, and other required application materials.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ScholarshipDetailsPage;
